// v2/hooks/usePagoWebpay.js
//
// Maneja la confirmación del callback Webpay después del redirect.
// Se monta en la ruta /pago/return y consume el endpoint v2 idempotente.
// Soporta el caso IDEM_EN_CURSO con polling automático.

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import InscripcionV2 from '../service/inscripcion.service.js';
import VentaPresencialV2 from '../service/ventaPresencial.service.js';
import { codigoError, mensajeDeError } from '../service/client.js';

const MAX_REINTENTOS_IDEM = 6;
const INTERVAL_REINTENTO_MS = 1000;

/**
 * @param {object} opts
 * @param {'online'|'venta_presencial'} [opts.flujo='online']
 * @returns {{
 *   loading: boolean,
 *   resultado: object|null,  // { estado, pagoId|ventaPresencialId, inscripcionIds, ... }
 *   error: {code, mensaje}|null
 * }}
 */
export function usePagoWebpay({ flujo = 'online' } = {}) {
  const [params] = useSearchParams();
  const tokenWs = params.get('token_ws');

  const [loading, setLoading] = useState(true);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tokenWs) {
      setLoading(false);
      setError({ code: 'PAGO_INPUT_INVALIDO', mensaje: 'Falta token_ws en la URL' });
      return;
    }
    let cancelado = false;
    let intentos = 0;

    async function confirmar() {
      try {
        const fn = flujo === 'venta_presencial'
          ? VentaPresencialV2.confirmarPagoWebpay
          : InscripcionV2.confirmarPago;
        const out = await fn(tokenWs);
        if (cancelado) return;
        setResultado(out);
        setError(null);
      } catch (err) {
        if (cancelado) return;
        const code = codigoError(err);
        if (code === 'IDEM_EN_CURSO' && intentos < MAX_REINTENTOS_IDEM) {
          intentos++;
          setTimeout(confirmar, INTERVAL_REINTENTO_MS);
          return;
        }
        setError({ code, mensaje: mensajeDeError(err) });
      } finally {
        if (!cancelado) setLoading(false);
      }
    }
    confirmar();
    return () => { cancelado = true; };
  }, [tokenWs, flujo]);

  return { loading, resultado, error };
}

export default usePagoWebpay;
